import "./document.css"

import Header from "../../assets/components/01_header/header";
import Footer from "../../assets/components/02_footer/footer";

export default function PrivacyPolicies() {
    return (
        <div>
            <Header />
            <div className="documentation">
                <h1 className="BlogTitle">Privacy Policy</h1>
                <p><strong>Last Updated:</strong> 21 February 2026</p>

                <p>
                This Privacy Policy describes how Adstrate ("Agency", "we", "us", or "our") collects, uses, stores, shares, and protects personal information obtained through our website, digital platforms, and related communications (collectively, the "Services"). By accessing or using our Services, you agree to the practices described in this Privacy Policy.
                </p>

                <h2>1. Introduction</h2>
                <p>
                Adstrate respects your privacy and is committed to protecting the personal information you share with us. We process personal data in accordance with applicable data protection laws, including but not limited to the Digital Personal Data Protection Act, 2023 (India), and, where applicable, the General Data Protection Regulation (GDPR) and other international privacy standards.
                </p>

                <h2>2. Information We Collect</h2>
                <p>We may collect the following categories of personal and non-personal information:</p>

                <h3>2.1 Personal Information</h3>
                <ul>
                <li>Full name</li>
                <li>Email address</li>
                <li>Phone number</li>
                <li>Company or business name</li>
                <li>Job title or professional role</li>
                </ul>

                <h3>2.2 Communication Data</h3>
                <ul>
                <li>Information submitted via website contact forms, lead generation forms, inquiry forms, or onboarding documents</li>
                <li>Emails, messages, call records, or other communications sent to or received from us</li>
                </ul>

                <h3>2.3 Technical and Usage Information</h3>
                <ul>
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device type, operating system, and screen resolution</li>
                <li>Pages visited, time spent on pages, clickstream data, and referring URLs</li>
                </ul>

                <h2>3. How We Collect Information</h2>
                <ul>
                <li>Website contact, inquiry, and lead generation forms</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Analytics, performance monitoring, and marketing tools</li>
                <li>Email, phone, video calls, and direct communication</li>
                <li>Proposal requests, onboarding forms, contracts, invoices, and service-related documents</li>
                <li>
                    Publicly available sources, including business websites, online directories, and platforms such as search engines and business listing services (e.g., publicly listed phone numbers, email addresses, company information, and professional details), collected solely for business-to-business lead generation and outreach, in accordance with applicable law
                </li>
                </ul>

                <h2>4. Legal Basis for Processing Personal Data</h2>
                <ul>
                <li><strong>Consent:</strong> Where you have explicitly provided consent</li>
                <li><strong>Contractual necessity:</strong> To perform a contract or take pre-contractual steps at your request</li>
                <li><strong>Legal obligation:</strong> To comply with applicable laws and regulations</li>
                <li><strong>Legitimate interests:</strong> For business operations, service improvement, fraud prevention, and communications, provided such interests do not override your fundamental rights</li>
                </ul>

                <h2>5. Purpose of Data Collection</h2>
                <ul>
                <li>Responding to inquiries and information requests</li>
                <li>Providing, managing, and delivering our services</li>
                <li>Preparing proposals, contracts, invoices, and related documentation</li>
                <li>Client onboarding and account management</li>
                <li>Internal record-keeping and administrative purposes</li>
                <li>Improving website performance, functionality, and user experience</li>
                <li>Analytics, reporting, and performance monitoring</li>
                <li>Marketing and promotional communications (only where consent has been provided or where permitted by law)</li>
                </ul>

                <h2>6. Cookies and Tracking Technologies</h2>
                <ul>
                <li>Ensure proper website functionality</li>
                <li>Analyze website traffic and user behavior</li>
                <li>Measure marketing effectiveness and campaign performance</li>
                </ul>
                <p>
                You may control or disable cookies through your browser settings. Please note that disabling cookies may affect the availability or functionality of certain website features.
                </p>

                <h2>7. Data Sharing and Disclosure</h2>
                <p>
                We do not sell, rent, or trade personal data. We may share information only with trusted third parties strictly necessary to operate our business, including:
                </p>
                <ul>
                <li>Website hosting and cloud service providers</li>
                <li>Customer relationship management (CRM) platforms</li>
                <li>Payment processors and accounting software providers</li>
                <li>Analytics, advertising, and marketing technology partners</li>
                </ul>
                <p>
                All third parties are contractually required to maintain appropriate confidentiality, security, and data protection standards.
                </p>

                <h2>8. International Data Transfers</h2>
                <p>
                If you access our Services from outside India, your information may be transferred to, stored, or processed in India or other jurisdictions where our service providers operate. We take reasonable steps to ensure that such transfers comply with applicable data protection laws and that adequate safeguards are in place.
                </p>

                <h2>9. Data Retention</h2>
                <p>We retain personal data only for as long as necessary to:</p>
                <ul>
                <li>Fulfill the purposes outlined in this Privacy Policy</li>
                <li>Comply with legal, regulatory, or contractual obligations</li>
                <li>Support ongoing or potential business relationships</li>
                </ul>
                <p>
                When personal data is no longer required, it is securely deleted, anonymized, or archived in accordance with applicable laws and internal policies.
                </p>

                <h2>10. Data Security</h2>
                <p>
                We implement reasonable technical and organizational measures to protect personal data against unauthorized access, disclosure, alteration, or destruction. These measures include access controls, secure storage practices, and internal data handling protocols.
                </p>
                <p>
                However, no method of transmission over the internet or electronic storage is completely secure. While we strive to protect your data, we cannot guarantee absolute security.
                </p>

                <h2>11. User Rights and Choices</h2>
                <p>Subject to applicable law, you have the right to:</p>
                <ul>
                <li>Request access to your personal data</li>
                <li>Request correction or updating of inaccurate or incomplete data</li>
                <li>Request deletion or erasure of your personal data</li>
                <li>Withdraw consent for marketing communications at any time</li>
                <li>Object to or restrict certain processing activities, where legally permitted</li>
                </ul>
                <p>
                Requests may be submitted by contacting us at <a href="mailto:contact@adstrate.in">contact@adstrate.in</a>. We will respond within the timeframes required by applicable law.
                </p>

                <h2>12. Grievance Redressal</h2>
                <p>
                If you believe your data protection rights have been violated or have concerns regarding our processing of personal data, you may submit a grievance by emailing <a href="mailto:contact@adstrate.in">contact@adstrate.in</a>. We will investigate and respond to grievances in accordance with applicable legal requirements.
                </p>

                <h2>13. Automated Decision-Making</h2>
                <p>
                We do not use personal data for automated decision-making or profiling that produces legal or similarly significant effects on individuals.
                </p>

                <h2>14. Children’s Privacy</h2>
                <p>
                Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal data from children. If we become aware that personal data of a minor has been collected inadvertently, we will take steps to delete such information promptly.
                </p>

                <h2>15. Third-Party Links</h2>
                <p>
                Our website may contain links to third-party websites or services. We are not responsible for the privacy practices, content, or data handling policies of external websites. Users are encouraged to review the privacy policies of such third parties independently.
                </p>

                <h2>16. Changes to This Privacy Policy</h2>
                <p>
                We reserve the right to update or modify this Privacy Policy at any time. Changes will be posted on this page with a revised “Last Updated” date. Continued use of our Services after changes are posted constitutes acceptance of the updated Privacy Policy.
                </p>

                <h2>17. Contact Information</h2>
                <p>
                For questions, requests, or concerns regarding this Privacy Policy or personal data processing, please contact:
                </p>
                <p>
                <strong>Email:</strong> <a href="mailto:contact@adstrate.in">contact@adstrate.in</a><br />
                <strong>Agency Name:</strong> Adstrate
                </p>

                <p>
                This Privacy Policy is intended to provide transparency regarding our data practices and does not create contractual rights beyond those provided under applicable law.
                </p>
            </div>
            <Footer />
        </div>
    );
}